// Shop TV (waiting room): every truck in the shop, its jobs, who is on them, and when it's ready. Opened with a secret link, no sign-in.
import { useEffect, useState } from 'react';

type Job = { title: string; status: string; tech: string; started_at: string | null; worked_sec: number };
type Truck = { unit: string; unit_type: string; customer: string; vin6: string; trailer_unit: string; bay: string; urgent: boolean; status: string; since: string; jobs: Job[] };
type Data = { now: string; shop: string; trucks: Truck[] };

const PER_PAGE = 8;
const dur = (sec: number) => { const m = Math.floor(sec / 60); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };
const jobSec = (j: Job, skew: number) => j.worked_sec + (j.started_at ? Math.max(0, (Date.now() + skew - Date.parse(j.started_at)) / 1000) : 0);

function truckState(t: Truck): [string, string] {
  if (t.status === 'Ready') return ['ready', '✅ Ready for pickup'];
  const js = t.jobs;
  if (js.length && js.every(j => j.status === 'done')) return ['ready', '✅ Work done'];
  if (js.some(j => j.status === 'doing' && j.started_at)) return ['work', '🔧 Working'];
  if (js.some(j => j.status === 'waiting')) return ['wait', '⏸ Waiting for parts'];
  if (js.some(j => j.status === 'done')) return ['work', '🔧 In progress'];
  return ['line', '🕒 In line'];
}

export function ShopTv({ token }: { token: string }) {
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState('');
  const [skew, setSkew] = useState(0);
  const [page, setPage] = useState(0);
  const [, tick] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`/api/tv/${encodeURIComponent(token)}`, { cache: 'no-store' });
        const d = await r.json();
        if (!alive) return;
        if (!r.ok) { setErr(d.error || 'This TV link is turned off.'); setData(null); return; }
        setErr(''); setData(d); setSkew(Date.parse(d.now) - Date.now());
      } catch { /* offline for a moment — keep the last screen */ }
    };
    void load();
    const a = setInterval(load, 30000), b = setInterval(() => tick(x => x + 1), 1000);
    document.title = 'Shop TV';
    return () => { alive = false; clearInterval(a); clearInterval(b); };
  }, [token]);
  const trucks = data?.trucks || [];
  const pages = Math.max(1, Math.ceil(trucks.length / PER_PAGE));
  useEffect(() => { const t = setInterval(() => setPage(p => (p + 1) % pages), 15000); return () => clearInterval(t); }, [pages]);
  const nowD = new Date(Date.now() + skew);
  if (err) return <div className="tv tv-off"><h1>📺 Shop TV</h1><p>{err}</p></div>;
  if (!data) return <div className="tv tv-off"><p>Loading…</p></div>;
  const all = trucks.flatMap(t => t.jobs);
  const states = trucks.map(truckState);
  const shown = trucks.slice((page % pages) * PER_PAGE, (page % pages) * PER_PAGE + PER_PAGE);
  return (
    <div className="tv">
      <header className="tv-top">
        <div><b className="tv-shop">{data.shop}</b><span className="tv-sub">Trucks in the shop</span></div>
        <div className="tv-sum">
          <span><b>{trucks.length}</b> in shop</span>
          <span className="w"><b>{all.filter(j => j.status === 'doing' && j.started_at).length}</b> jobs working</span>
          <span className="r"><b>{states.filter(s => s[0] === 'ready').length}</b> ready</span>
        </div>
        <div className="tv-clock">{nowD.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}<small>{nowD.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</small></div>
      </header>
      {!trucks.length ? <p className="tv-empty">No trucks in the shop right now.</p> : <main className="tv-grid">
        {shown.map((t, i) => {
          const [k, label] = truckState(t);
          return (
            <section key={i} className={'tv-card ' + k + (t.urgent ? ' urgent' : '')}>
              <div className="tv-card-head">
                <div className="tv-id"><b className="tv-unit">#{t.unit || '—'}</b>{t.vin6 && <span className="tv-vin">VIN …{t.vin6}</span>}</div>
                <span className={'tv-state ' + k}>{label}</span>
              </div>
              <div className="tv-cust">{t.customer}{t.trailer_unit ? <span> · + trailer #{t.trailer_unit}</span> : null}{t.bay ? <span className="tv-bay">{t.bay}</span> : null}</div>
              {t.jobs.length ? <ul className="tv-jobs">{t.jobs.map((j, n) => {
                const run = j.status === 'doing' && !!j.started_at;
                const st = run ? 'run' : j.status === 'waiting' ? 'wait' : j.status === 'done' ? 'done' : 'todo';
                return <li key={n} className={st}>
                  <span className="tv-dot" /><span className="tv-title">{j.title}</span>
                  <span className="tv-who">{st === 'done' ? '✓ Done' : st === 'wait' ? '⏸ Parts' : j.tech ? `${j.tech}${run ? ' · ' + dur(jobSec(j, skew)) : ''}` : 'Next'}</span>
                </li>;
              })}</ul> : <p className="tv-none">Checked in — jobs coming</p>}
            </section>
          );
        })}
      </main>}
      {pages > 1 && <div className="tv-pages">{Array.from({ length: pages }, (_, p) => <i key={p} className={p === page % pages ? 'on' : ''} />)}</div>}
    </div>
  );
}
